import { Color } from '@persona-id/design-tokens';
import { DateTime } from 'luxon';
import { ReactNode } from 'react';
import styled from 'styled-components';

import AttributeItem, { AttributeGrid } from 'dashboard/components/AttributeItem';
import {
  MatchIconAlert,
  MatchIconPositive,
  MatchIconWarning,
} from 'dashboard/components/ReportResult/MatchIcons';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import TableLayout, { TableIconTD, TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import {
  EmailAddressReputation,
  EmailDomainReputation,
  ReportEmailAddress,
} from 'dashboard/models/report/email-address';
import Card from 'design-system/components/Card';
import { BASE } from 'design-system/variables/spacing';
import { ABBR_DATE_FORMAT } from 'lib/constants/luxonFormats';

const NotApplicableText = styled('span')`
  color: ${Color.CoreSlate1000};
`;

const RiskAssessmentTH = styled(TableTH)`
  width: ${BASE * 30}px;
`;

interface EmailAddressProps {
  report: ReportEmailAddress;
  multiCardFormat: boolean;
}

const formatReputationString = (text: EmailAddressReputation | EmailDomainReputation) => {
  if (text.toUpperCase() === 'N/A') {
    return text.toUpperCase();
  }
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const getEmailReputationIcon = (emailReputation: EmailAddressReputation): ReactNode => {
  switch (emailReputation) {
    case EmailAddressReputation.HIGH:
      return <MatchIconPositive />;
    case EmailAddressReputation.MEDIUM:
      return <MatchIconAlert />;
    case EmailAddressReputation.LOW:
    case EmailAddressReputation.NONE:
    default:
      return <MatchIconAlert />;
  }
};

const getEmailDomainReputationIcon = (emailDomainReputation: EmailDomainReputation): ReactNode => {
  switch (emailDomainReputation) {
    case EmailDomainReputation.HIGH:
      return <MatchIconPositive />;
    case EmailDomainReputation.MEDIUM:
      return <MatchIconWarning />;
    case EmailDomainReputation.LOW:
    case EmailDomainReputation.NA:
    default:
      return <MatchIconAlert />;
  }
};

export const EmailAddress = ({ report, multiCardFormat = false }: EmailAddressProps) => (
  <ReportResultLayout multiCardFormat={multiCardFormat}>
    <RiskAssessment report={report} />
    <EmailInformation report={report} />
  </ReportResultLayout>
);

const RiskAssessment = ({ report }: { report: ReportEmailAddress }) => {
  const {
    'email-reputation': emailReputation,
    'email-domain-reputation': emailDomainReputation,
    'email-is-suspicious': emailIsSuspicious,
    'email-is-blocklisted': emailIsBlocklisted,
    'email-is-spam': emailIsSpam,
  } = report.attributes;

  return (
    <>
      <Card.Title>Risk Assessment</Card.Title>
      <TableLayout
        dataTest='risk-assessment-table'
        isLoading={false}
        showHeader={true}
        isEmpty={false}
        nullState={null}
        headerTR={
          <>
            <TableTH data-test='risk-assessment-table__icon-header' />
            <RiskAssessmentTH data-test='risk-assessment-table__risk-property-header'>
              Risk Property
            </RiskAssessmentTH>
            <TableTH data-test='risk-assessment-table__value-header'>Value</TableTH>
          </>
        }
      >
        <TableTR
          data-test='risk-assessment-table__row--email-is-suspicious'
          id='risk-assessment-table__row--email-is-suspicious'
          key='email-is-suspicious'
        >
          <TableIconTD>
            {emailIsSuspicious ? <MatchIconAlert /> : <MatchIconPositive />}
          </TableIconTD>
          <TableTD>Suspicious</TableTD>
          <TableTD>{emailIsSuspicious ? 'Yes' : 'No'}</TableTD>
        </TableTR>
        <TableTR
          data-test='risk-assessment-table__row--email-reputation'
          id='risk-assessment-table__row--email-reputation'
          key='email-reputation'
        >
          <TableIconTD>{emailReputation && getEmailReputationIcon(emailReputation)}</TableIconTD>
          <TableTD>Email Reputation</TableTD>
          {emailReputation && (
            <TableTD>
              {emailReputation.toUpperCase() === 'N/A' ? (
                <NotApplicableText>{formatReputationString(emailReputation)}</NotApplicableText>
              ) : (
                formatReputationString(emailReputation)
              )}
            </TableTD>
          )}
        </TableTR>
        {emailDomainReputation != null && (
          <TableTR
            data-test='risk-assessment-table__row--email-domain-reputation'
            id='risk-assessment-table__row--email-domain-reputation'
            key='email-domain-reputation'
          >
            <TableIconTD>{getEmailDomainReputationIcon(emailDomainReputation)}</TableIconTD>
            <TableTD>Email Domain Reputation</TableTD>
            <TableTD>
              {emailDomainReputation.toUpperCase() === 'N/A' ? (
                <NotApplicableText>
                  {formatReputationString(emailDomainReputation)}
                </NotApplicableText>
              ) : (
                formatReputationString(emailDomainReputation)
              )}
            </TableTD>
          </TableTR>
        )}
        <TableTR
          data-test='risk-assessment-table__row--email-is-spam'
          id='risk-assessment-table__row--email-is-spam'
          key='email-is-spam'
        >
          <TableIconTD>{emailIsSpam ? <MatchIconAlert /> : <MatchIconPositive />}</TableIconTD>
          <TableTD>Spam {emailIsBlocklisted == null && 'Trap'}</TableTD>
          <TableTD>{emailIsSpam ? 'Yes' : 'No'}</TableTD>
        </TableTR>
        {emailIsBlocklisted != null && (
          <TableTR
            data-test='risk-assessment-table__row--email-is-blocklisted'
            id='risk-assessment-table__row--email-is-blocklisted'
            key='email-is-blocklisted'
          >
            <TableIconTD>
              {emailIsBlocklisted ? <MatchIconAlert /> : <MatchIconPositive />}
            </TableIconTD>
            <TableTD>Blocklisted</TableTD>
            <TableTD>{emailIsBlocklisted ? 'Yes' : 'No'}</TableTD>
          </TableTR>
        )}
      </TableLayout>
    </>
  );
};

const EmailInformation = ({ report }: { report: ReportEmailAddress }) => {
  const {
    'email-domain': emailDomain,
    'email-reference-count': emailReferenceCount,
    'email-domain-age-days': emailDomainAgeDays,
    'email-first-seen-days': emailFirstSeenDays,
    'email-last-seen-days': emailLastSeenDays,
    'email-is-disposable': emailIsDisposable,
    'email-is-deliverable': emailIsDeliverable,
    'email-is-valid-mx': emailIsValidMx,
    'email-domain-exists': emailDomainExists,
    'email-is-free-provider': emailIsFreeProvider,
    'email-malicious-activity-detected-recent': emailMaliciousActivityDetectedRecent,
    'email-credentials-leaked-recent': emailCredentialsLeakedRecent,
    'completed-at': completedAt,
  } = report.attributes;

  return (
    <>
      <Card.Title>Email information</Card.Title>
      <AttributeGrid data-chromatic='ignore'>
        {emailReferenceCount != null && (
          <AttributeItem title='Reference count' value={emailReferenceCount} placeholder='N/A' />
        )}
        <AttributeItem
          title='Deliverable'
          value={emailIsDeliverable ? 'Yes' : 'No'}
          placeholder='N/A'
        />
        {emailIsFreeProvider != null && (
          <AttributeItem
            title='Free Provider'
            value={emailIsFreeProvider ? 'Yes' : 'No'}
            placeholder='N/A'
          />
        )}
        <AttributeItem
          title='Domain Created'
          data-chromatic='ignore'
          value={
            emailDomainAgeDays != null
              ? DateTime.fromISO(completedAt)
                  .minus({ days: emailDomainAgeDays })
                  .toFormat(ABBR_DATE_FORMAT) +
                ` (${
                  emailDomainAgeDays +
                  Math.floor(DateTime.local().diff(DateTime.fromISO(completedAt), 'days').days)
                } days ago)`
              : null
          }
          placeholder='N/A'
        />
        <AttributeItem
          title='First seen'
          data-chromatic='ignore'
          value={
            emailFirstSeenDays != null
              ? DateTime.fromISO(completedAt)
                  .minus({ days: emailFirstSeenDays })
                  .toFormat(ABBR_DATE_FORMAT) +
                ` (${
                  emailFirstSeenDays +
                  Math.floor(DateTime.local().diff(DateTime.fromISO(completedAt), 'days').days)
                } days ago)`
              : null
          }
          placeholder='N/A'
        />
        {emailLastSeenDays != null && (
          <AttributeItem
            title='Last seen'
            data-chromatic='ignore'
            value={
              DateTime.fromISO(completedAt)
                .minus({ days: emailLastSeenDays })
                .toFormat(ABBR_DATE_FORMAT) +
              ` (${
                emailLastSeenDays +
                Math.floor(DateTime.local().diff(DateTime.fromISO(completedAt), 'days').days)
              } days ago)`
            }
            placeholder='N/A'
          />
        )}
        <AttributeItem
          title='Disposable'
          value={emailIsDisposable ? 'Yes' : 'No'}
          placeholder='N/A'
        />
        <AttributeItem title='Domain' value={emailDomain} placeholder='N/A' />
        <AttributeItem
          title='Domain Exists'
          value={emailDomainExists ? 'Yes' : 'No'}
          placeholder='N/A'
        />
        <AttributeItem title='Valid MX' value={emailIsValidMx ? 'Yes' : 'No'} placeholder='N/A' />
        {emailMaliciousActivityDetectedRecent != null && (
          <AttributeItem
            title='Recent Malicious Activity'
            value={emailMaliciousActivityDetectedRecent ? 'Yes' : 'No'}
            placeholder='N/A'
          />
        )}
        {emailCredentialsLeakedRecent != null && (
          <AttributeItem
            title='Recently Leaked'
            value={emailCredentialsLeakedRecent ? 'Yes' : 'No'}
            placeholder='N/A'
          />
        )}
      </AttributeGrid>
    </>
  );
};
